import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { BaseSimpleDialog } from "../../components/dialogs/BaseSimpleDialog"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { DataFileGroup } from "../../types/generated/api/DataFileGroup"
import type { StandardErrorType } from "../../utils/ErrorHandling"
import type { MenuOption } from "../Members"
import { DataFileCard } from "./DataFileCard"
import type { DataFileContainerProps } from "./interfaces"

export function Container(props: Readonly<DataFileContainerProps>): React.ReactElement {
  const { dataFile } = props
  const { id: projectId } = useParams()

  const { t } = useTranslation("datafiles")

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)

  // retrieve Data File
  const { refetch } = useQuery<AxiosResponse<DataFileGroup>>(
    ["data-file-retrieve", projectId, dataFile.uuid],
    () => KonanAPI.fetchDataFile(projectId as string, dataFile.uuid),
    {
      enabled: false,
      // Downloading file from pre-signed url
      onSuccess: ({ data }) => {
        const link = document.createElement("a")
        link.href = data?.active_version.data_file

        // Start download
        link.click()

        // Clean up and remove the link
        link.remove()
      },
    },
  )

  const deleteDataFileMutation = useMutation<
    AxiosResponse,
    AxiosError<StandardErrorType>,
    {
      project_uuid: string
      data_file_uuid: string
    }
  >(({ data_file_uuid, project_uuid }) => KonanAPI.deleteDataFile(project_uuid, data_file_uuid), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["data-files", projectId])

      NotificationUtils.toast(t("dataFileContainer.deleteSuccess"), { severity: "success" })
    },
  })

  const menuOptions = (): MenuOption[] => {
    const options: MenuOption[] = []

    options.push({
      title: t("dataFileContainer.copyId"),
      variant: "base",
      onClick: () => {
        navigator.clipboard.writeText(dataFile.uuid)
        NotificationUtils.toast(t("dataFileContainer.copied"), { severity: "success" })
      },
    })

    options.push({
      title: t("dataFileContainer.deleteFile"),
      variant: "negative",
      onClick: () => {
        setIsDeletionDialogOpen(true)
      },
    })

    return options
  }

  const onDialogAccept = async (): Promise<void> => {
    await deleteDataFileMutation.mutateAsync({
      project_uuid: projectId as string,
      data_file_uuid: dataFile.uuid,
    })

    setIsDeletionDialogOpen(false)
  }

  return (
    <Fragment>
      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          isLoading={deleteDataFileMutation.isLoading}
          onAccept={onDialogAccept}
          mode={"data-file-deletion"}
          name={dataFile.name}
          open
          onClose={() => setIsDeletionDialogOpen(false)}
        />
      )}

      <DataFileCard
        dataFile={dataFile}
        onDownloadInitiated={() => {
          refetch()
          NotificationUtils.toast(t("dataFileContainer.downloadStarting"), { severity: "info" })
        }}
        menuOptions={menuOptions()}
      />
    </Fragment>
  )
}
